import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

const AgedPayablesReport = () => {
  const [payables, setPayables] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAgedPayables();
  }, []);

  const fetchAgedPayables = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      // Fetch aged payables
      const payablesResponse = await axios.get('/api/bills/aged-payables', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch outstanding summary
      const summaryResponse = await axios.get('/api/bills/outstanding-summary', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPayables(payablesResponse.data.payables || []);
      setSummary(summaryResponse.data.summary || []);
      setError('');
    } catch (err) {
      setError('Failed to load report: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const getAgingColor = (bucket) => {
    switch (bucket) {
      case 'Current':
        return 'success';
      case '1-30 days':
        return 'info';
      case '31-60 days':
        return 'warning';
      case '61-90 days':
        return 'warning';
      case '90+ days':
        return 'error';
      default:
        return 'default';
    }
  };

  const calculateTotals = () => {
    const totals = {
      current: 0,
      '1-30': 0,
      '31-60': 0,
      '61-90': 0,
      '90plus': 0,
      total: 0
    };

    payables.forEach(p => {
      const amount = parseFloat(p.outstanding_amount);
      totals.total += amount;
      
      switch (p.aging_bucket) {
        case 'Current':
          totals.current += amount;
          break;
        case '1-30 days':
          totals['1-30'] += amount;
          break;
        case '31-60 days':
          totals['31-60'] += amount;
          break;
        case '61-90 days':
          totals['61-90'] += amount;
          break;
        case '90+ days':
          totals['90plus'] += amount;
          break;
      }
    });

    return totals;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const totals = calculateTotals();

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Aged Payables Report
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Current
              </Typography>
              <Typography variant="h6" color="success.main">
                {formatCurrency(totals.current)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                1-30 Days
              </Typography>
              <Typography variant="h6" color="info.main">
                {formatCurrency(totals['1-30'])}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                31-60 Days
              </Typography>
              <Typography variant="h6" color="warning.main">
                {formatCurrency(totals['31-60'])}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                61-90 Days
              </Typography>
              <Typography variant="h6" color="warning.main">
                {formatCurrency(totals['61-90'])}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                90+ Days
              </Typography>
              <Typography variant="h6" color="error.main">
                {formatCurrency(totals['90plus'])}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Total Outstanding */}
      <Card sx={{ mb: 3, bgcolor: 'primary.light' }}>
        <CardContent>
          <Typography variant="h5" color="primary.contrastText">
            Total Outstanding: {formatCurrency(totals.total)}
          </Typography>
        </CardContent>
      </Card>

      {/* Detailed Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Supplier</TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell>Invoice Date</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="center">Days</TableCell>
              <TableCell>Aging</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payables.map((payable) => (
              <TableRow key={payable.bill_id}>
                <TableCell>{payable.supplier_name}</TableCell>
                <TableCell>{payable.invoice_number}</TableCell>
                <TableCell>
                  {new Date(payable.invoice_date).toLocaleDateString('en-GB')}
                </TableCell>
                <TableCell>
                  {new Date(payable.due_date).toLocaleDateString('en-GB')}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(payable.outstanding_amount)}
                </TableCell>
                <TableCell align="center">
                  <Typography
                    variant="body2"
                    color={payable.days_outstanding > 60 ? 'error' : 'textPrimary'}
                    fontWeight={payable.days_outstanding > 60 ? 'bold' : 'normal'}
                  >
                    {payable.days_outstanding}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={payable.aging_bucket}
                    color={getAgingColor(payable.aging_bucket)}
                    size="small"
                    icon={payable.days_outstanding > 90 ? <WarningIcon /> : undefined}
                  />
                </TableCell>
              </TableRow>
            ))}
            {payables.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ py: 4 }}>
                    <CheckIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                      No outstanding payables! 🎉
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Outstanding by Supplier */}
      {summary && summary.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Outstanding by Supplier
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Supplier</TableCell>
                  <TableCell align="center">Unpaid Bills</TableCell>
                  <TableCell align="right">Total Outstanding</TableCell>
                  <TableCell>Oldest Invoice</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.map((s) => (
                  <TableRow key={s.supplier_id}>
                    <TableCell>{s.supplier_name}</TableCell>
                    <TableCell align="center">{s.unpaid_count}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(s.total_outstanding)}
                    </TableCell>
                    <TableCell>
                      {new Date(s.oldest_invoice_date).toLocaleDateString('en-GB')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default AgedPayablesReport;